import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate } from '../util/datetime';
import { useSortedRows } from '../util/tableSort';
import { SortHeader } from './SortHeader';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';

import type { Invitation } from '../api/invitations';

const NO_ROWS: Invitation[] = [];
const INVITE_SORT = {
  email: (i: Invitation) => i.email,
  invited: (i: Invitation) => i.created_at,
  expires: (i: Invitation) => i.expires_at,
};
import { extractErrorMessage } from '../api/errors';

/**
 * The three operations the panel needs, injected by the caller so the
 * same UI drives both tenant-scoped invites (GET/POST/DELETE under
 * /tenants/{id}/invitations) and platform-level superadmin invites
 * (under /admin/invitations). `list` returns the already-scoped,
 * already-filtered rows to show; `resend` re-issues one (the backend
 * revokes the old row + mails a fresh link); `cancel` revokes it.
 */
export interface InvitationSource {
  list: () => Promise<Invitation[]>;
  resend: (inv: Invitation) => Promise<void>;
  cancel: (inv: Invitation) => Promise<void>;
}

/**
 * Pending-invitations panel — invited-but-not-yet-joined people.
 *
 * A magic-link invitee has NO membership until they accept, so they
 * never show up in a roster (rosters list memberships). This panel
 * surfaces the other half — the open invitations — so an operator can
 * see who's outstanding and nudge or cancel them.
 *
 * Renders nothing while loading or when there are zero pending invites,
 * so it stays invisible on tenants that use direct-add (no invites) or
 * a platform with everyone already onboarded.
 *
 * `reloadKey` lets the parent force a refetch after it sends a new
 * invite (the roster reload and this panel are separate fetches).
 */
export function PendingInvitations({
  source,
  canWrite,
  reloadKey = 0,
  embedded = false,
}: {
  source: InvitationSource;
  canWrite: boolean;
  reloadKey?: number;
  /**
   * Render inline (a top divider + the content, no own Paper) so the caller
   * can fold this panel into an enclosing card. Defaults to the standalone
   * outlined-Paper card.
   */
  embedded?: boolean;
}) {
  const [rows, setRows] = useState<Invitation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [resentTo, setResentTo] = useState<string | null>(null);

  // Hold the latest source in a ref so reload/resend/cancel stay stable
  // across renders — the caller passes an inline object whose identity
  // changes every render, which would otherwise loop the fetch effect.
  const sourceRef = useRef(source);
  sourceRef.current = source;

  const reload = useCallback(async () => {
    try {
      setRows(await sourceRef.current.list());
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load pending invitations'));
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, reloadKey]);

  const resend = async (inv: Invitation) => {
    setBusyId(inv.id);
    setError(null);
    setResentTo(null);
    try {
      await sourceRef.current.resend(inv);
      setResentTo(inv.email);
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to resend invitation'));
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (inv: Invitation) => {
    setBusyId(inv.id);
    setError(null);
    try {
      await sourceRef.current.cancel(inv);
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to cancel invitation'));
    } finally {
      setBusyId(null);
    }
  };

  const { sort, sorted } = useSortedRows(rows ?? NO_ROWS, INVITE_SORT, 'invited', 'desc');

  // Stay invisible until we know there's something to show — no empty
  // card on the common "everyone's joined" / direct-add case.
  if (rows === null || rows.length === 0) return null;

  const now = Date.now();

  const inner = (
    <>
      <Stack sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="h6">Pending invitations</Typography>
        <Typography variant="body2" color="text.secondary">
          Invited but not yet joined. Resend the email or cancel the
          invitation.
        </Typography>
      </Stack>

      {resentTo && (
        <Alert
          severity="success"
          sx={{ mx: 2, mb: 1 }}
          onClose={() => setResentTo(null)}
        >
          Invitation re-sent to <strong>{resentTo}</strong>. A fresh link
          expires in 7 days.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortHeader label="Email" col="email" sort={sort} />
              <SortHeader label="Invited" col="invited" sort={sort} />
              <SortHeader label="Expires" col="expires" sort={sort} />
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((inv) => {
              const expired = new Date(inv.expires_at).getTime() < now;
              return (
                <TableRow key={inv.id} hover>
                  <TableCell>{inv.email}</TableCell>
                  <TableCell>
                    {formatDate(inv.created_at)}
                  </TableCell>
                  <TableCell>
                    {expired ? (
                      <Chip label="Expired" size="small" color="error" />
                    ) : (
                      formatDate(inv.expires_at)
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Resend invitation email">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => void resend(inv)}
                          aria-label={`Resend invitation to ${inv.email}`}
                          disabled={!canWrite || busyId === inv.id}
                        >
                          <ForwardToInboxIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Cancel invitation">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => void cancel(inv)}
                          aria-label={`Cancel invitation to ${inv.email}`}
                          disabled={!canWrite || busyId === inv.id}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  // Embedded: inline into an enclosing card, set off by a top divider.
  if (embedded) {
    return (
      <Box>
        <Divider />
        {inner}
      </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 0 }}>
      {inner}
    </Paper>
  );
}
